import { useCallback, useEffect, useRef, useState } from 'react';
import { getSearch, getValidToken } from '../services/getSearch';

const MAX_CONVERSATION_TURNS = 5;
const CONVERSATION_STORAGE_KEY = 'guru_conversation_v1';
const FIRST_CHUNK_TIMEOUT_MS = 55_000;
const STREAM_TIMEOUT_MS = 120_000;
const MAX_HISTORY_CHARS = 1500;

function loadConversationFromStorage() {
  try {
    const raw = sessionStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.map(turn => ({
      ...turn,
      hits: Array.isArray(turn.hits) ? turn.hits : [],
      isStreaming: false,
      notesReady: true,
    }));
  } catch {
    return [];
  }
}

function saveConversationToStorage(conversation) {
  try {
    const completedTurns = conversation.filter(turn => !turn.isStreaming);
    sessionStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(completedTurns));
  } catch {
    // sessionStorage puede estar lleno o no disponible.
  }
}

/**
 * Turnos anteriores completos para el agente (no tiene memoria: el contexto viaja en cada
 * invocación). Alterna usuario/asistente y omite turnos sin respuesta o con error.
 */
function buildHistory(conversation, excludeTurnId) {
  return conversation
    .filter(turn => turn.id !== excludeTurnId && !turn.isStreaming && !turn.answerError && turn.resume)
    .flatMap(turn => [
      { role: 'user', content: turn.query },
      { role: 'assistant', content: turn.resume.slice(0, MAX_HISTORY_CHARS) },
    ]);
}

/** Notas para las tarjetas desde el evento de fuentes (mismos campos que search_v3). */
function notesFromSources(sources) {
  const hits = Array.isArray(sources?.notas) ? sources.notas : [];
  return { hits, total: hits.length };
}

export function useConversationSearch() {
  const [conversation, setConversation] = useState(() => loadConversationFromStorage());
  const [showLoading, setShowLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const storageWasEmpty = useRef(conversation.length === 0);
  const conversationRef = useRef(conversation);
  const abortControllerRef = useRef(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    conversationRef.current = conversation;
    saveConversationToStorage(conversation);
  }, [conversation]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  const updateTurnById = useCallback((turnId, patch, expectedRunId) => {
    if (expectedRunId !== undefined && runIdRef.current !== expectedRunId) return;

    setConversation(previous => {
      if (expectedRunId !== undefined && runIdRef.current !== expectedRunId) {
        return previous;
      }

      return previous.map(turn => {
        if (turn.id !== turnId) return turn;
        const update = typeof patch === 'function' ? patch(turn) : patch;
        return { ...turn, ...update };
      });
    });
  }, []);

  const clearConversation = useCallback(() => {
    runIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    sessionStorage.removeItem(CONVERSATION_STORAGE_KEY);
    setConversation([]);
    setShowLoading(false);
    setIsSearching(false);
  }, []);

  const startNewConversation = useCallback(() => {
    clearConversation();
    setSessionId(() => crypto.randomUUID());
  }, [clearConversation]);

  const search = useCallback(async (query, { retryTurnId = null, origen = null } = {}) => {
    const currentQuery = query.trim();
    if (!currentQuery) {
      setShowLoading(false);
      return;
    }

    if (isSearching) return;

    const retryTurn = retryTurnId
      ? conversationRef.current.find(turn => turn.id === retryTurnId)
      : null;
    if (retryTurnId && !retryTurn) return;

    const lastTurn = conversationRef.current[conversationRef.current.length - 1];
    if (!retryTurn && lastTurn && lastTurn.query === currentQuery && !lastTurn.isStreaming) {
      setShowLoading(false);
      return;
    }

    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    const isCurrentRun = () => runIdRef.current === runId;
    const turnId = retryTurn?.id || `turn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const isFirstTurn = conversationRef.current.length === 0;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setShowLoading(isFirstTurn);
      setIsSearching(true);
      setConversation(previous => {
        if (!isCurrentRun()) return previous;

        if (retryTurn) {
          return previous.map(turn => (
            turn.id === turnId
              ? {
                ...turn,
                resume: '',
                isStreaming: true,
                hasReceivedChunk: false,
                answerError: null,
              }
              : turn
          ));
        }

        const next = [...previous, {
          id: turnId,
          query: currentQuery,
          resume: '',
          hits: [],
          total: 0,
          isStreaming: true,
          hasReceivedChunk: false,
          notesReady: false,
          answerError: null,
          notesError: null,
        }];
        return next.length > MAX_CONVERSATION_TURNS
          ? next.slice(next.length - MAX_CONVERSATION_TURNS)
          : next;
      });

      const history = buildHistory(conversationRef.current, turnId);
      const token = await getValidToken();
      if (!isCurrentRun() || controller.signal.aborted) return;

      const STREAM_CHUNK_BATCH_MS = 75;
      let resolveFirstChunk;
      const firstChunkPromise = new Promise(resolve => { resolveFirstChunk = resolve; });
      let firstChunkReceived = false;
      let streamFinished = false;
      let streamFailed = false;
      let firstChunkTimeoutId;
      let streamTimeoutId;
      let chunkBatchTimeoutId;
      let pendingStreamText = '';

      const flushPendingStreamText = () => {
        if (!isCurrentRun() || !pendingStreamText) return;

        const textToAppend = pendingStreamText;
        pendingStreamText = '';
        window.clearTimeout(chunkBatchTimeoutId);
        chunkBatchTimeoutId = undefined;
        updateTurnById(turnId, turn => ({
          resume: turn.resume + textToAppend,
        }), runId);
      };

      const scheduleStreamFlush = () => {
        if (!isCurrentRun() || chunkBatchTimeoutId) return;
        chunkBatchTimeoutId = window.setTimeout(
          flushPendingStreamText,
          STREAM_CHUNK_BATCH_MS
        );
      };

      const clearTimeouts = () => {
        window.clearTimeout(firstChunkTimeoutId);
        window.clearTimeout(streamTimeoutId);
        window.clearTimeout(chunkBatchTimeoutId);
        chunkBatchTimeoutId = undefined;
      };

      const reportStreamFailure = (message) => {
        if (!isCurrentRun() || streamFailed) return;
        flushPendingStreamText();
        streamFailed = true;
        updateTurnById(turnId, {
          isStreaming: false,
          notesReady: true,
          answerError: message,
        }, runId);
        resolveFirstChunk();
      };

      const timeoutStream = (message) => {
        if (!isCurrentRun() || streamFinished || streamFailed) return;
        if (!controller.signal.aborted) controller.abort();
        reportStreamFailure(message);
      };

      const onStreamChunk = (chunkContent) => {
        if (!isCurrentRun() || streamFailed || !chunkContent || typeof chunkContent !== 'string') {
          return;
        }

        pendingStreamText += chunkContent;
        scheduleStreamFlush();

        if (!firstChunkReceived) {
          firstChunkReceived = true;
          updateTurnById(turnId, { hasReceivedChunk: true }, runId);
          window.clearTimeout(firstChunkTimeoutId);
          resolveFirstChunk();
        }
      };

      firstChunkTimeoutId = window.setTimeout(() => {
        timeoutStream('La respuesta está tardando demasiado en comenzar. Inténtalo nuevamente.');
      }, FIRST_CHUNK_TIMEOUT_MS);
      streamTimeoutId = window.setTimeout(() => {
        timeoutStream('La respuesta tardó demasiado en completarse. Inténtalo nuevamente.');
      }, STREAM_TIMEOUT_MS);

      // Las notas llegan en el mismo stream, al final de la respuesta.
      const onSources = (sources) => {
        if (!isCurrentRun()) return;
        updateTurnById(turnId, {
          ...notesFromSources(sources),
          notesReady: true,
          notesError: null,
        }, runId);
      };

      const streamPromise = getSearch(currentQuery, {
        token,
        sessionId,
        history,
        origen,
        onChunk: onStreamChunk,
        onSources,
        signal: controller.signal,
      })
        .then(streamResponse => {
          const wasAborted = streamResponse?.aborted || streamResponse?.statusCode === 499;
          if (!isCurrentRun() || wasAborted || streamFailed) return;

          if (streamResponse?.statusCode !== 200) {
            reportStreamFailure('No fue posible completar la respuesta. Inténtalo nuevamente.');
          }
        })
        .catch(error => {
          const wasAborted = controller.signal.aborted
            || error?.name === 'AbortError'
            || error?.code === 'ERR_CANCELED';

          if (!isCurrentRun() || wasAborted || streamFailed) return;

          console.error('[useConversationSearch] Error en streaming:', error);
          reportStreamFailure('No fue posible completar la respuesta. Inténtalo nuevamente.');
        })
        .finally(() => {
          streamFinished = true;
          flushPendingStreamText();
          clearTimeouts();
          // Si no llegó el evento de fuentes, no dejar el skeleton de notas colgado.
          updateTurnById(turnId, { isStreaming: false, notesReady: true }, runId);
          resolveFirstChunk();
        });

      await streamPromise;
    } catch (error) {
      if (!isCurrentRun()) return;

      console.error('[useConversationSearch] Error en búsqueda:', error);
      updateTurnById(turnId, {
        isStreaming: false,
        notesReady: true,
        answerError: 'No fue posible iniciar la búsqueda. Inténtalo nuevamente.',
      }, runId);
    } finally {
      if (!isCurrentRun()) return;

      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setShowLoading(false);
      setIsSearching(false);
    }
  }, [isSearching, sessionId, updateTurnById]);

  return {
    conversation,
    showLoading,
    isSearching,
    storageWasEmpty: storageWasEmpty.current,
    search,
    clearConversation,
    startNewConversation,
  };
}
